// ── "New payment" | "Balance past due" ───────────────────────────────
//
// The owner, 2026-09-18 (P0c, verbatim): "the one off charge customer button
// should give an option if im collecting on a balance due or if its a new
// purchase for something. the option to collect on a balance due should be
// greyed out or not available if there is no balance past due. my thought is a
// slider at the top of the card one side is new payments and the other side is
// balance past due."
//
// This file owns the switch and everything the balance side changes about the
// existing Charge card on file modal. `charge-oneoff.js` still owns opening
// the modal, the card picker and the submit.
//
// ⚠️ "NEW PAYMENT" IS TODAY'S FORM, BYTE FOR BYTE. It sends no `purpose` at
// all, still requires a description, and has no ceiling on the amount. Every
// change here belongs to the other side of the switch.
//
// ⚠️ NEVER COLOUR ALONE (the owner is colorblind). The disabled side always
// carries a sentence saying why, and the side that is ON is also readable from
// the form underneath it — the unpaid payments appear, the description turns
// into a fixed line.
//
// ⚠️ A FAILED LOOKUP IS NOT "OWES NOTHING". The side is disabled either way
// (there is no balance we could safely charge against), but the sentence tells
// the truth rather than clearing a client who may well owe money.

import { callFunction } from '../api.js';
import { escHtml } from '../utils.js';
import { owedMoneyCents, owedDateLabel, owedDay, balanceHint } from './money.js';
import { chargeButtonParts, chargeButtonHtml } from './charge-button.js';
import { can } from './perm.js';
// The coach's own zone — same source owed-block.js's mountAmountOwedBlock
// reads, so this modal's dates and the profile's Amount owed block never
// disagree about which day an invoice landed on.
import { getCoachTimezone } from '../config.js';

const NO_BALANCE = 'No balance past due.';
const LOOKING = 'Checking for a balance past due…';
const COULD_NOT_CHECK = 'We could not check for a balance past due just now.';
/** The server writes the real receipt line; this is what the coach is shown. */
const BALANCE_DESCRIPTION = 'Payment toward balance';

/** @type {'new'|'balance'} */
let _mode = 'new';
/** "Pay with another card" (New purchase, part 3b): only the balance side may be sent; there is no switch. */
let _balanceOnly = false;
/** @type {any[]} */
let _owed = [];
let _totalCents = 0;
/** @type {{brand?: string, last4?: string}|null} */
let _fetchedCard = null;
let _lookupOk = false;
/** What "New payment" had typed in it before the coach flipped to the balance. */
let _newSide = { amount: '', description: '' };
/** @type {Date|null} */
let _now = null;
/** ⚠️ READ ONCE PER MODAL-OPEN, NOT PER RENDER — same rule owed-block.js's
 * mountAmountOwedBlock follows; the settings lookup behind getCoachTimezone()
 * hits localStorage, and a redraw must not change the answer mid-modal.
 * @type {string|undefined} */
let _timeZone;

const $ = (id) => document.getElementById(id);

export function chargeMode() { return _mode; }
/** The rows the modal was opened with — the only place the DATES live. */
export function owedRows() { return _owed; }
export function owedTotalCents() { return _totalCents; }
/** The zone this modal's dates were resolved in — for callers outside this
 * file (charge-oneoff.js) that also render an owed date or a balance sentence. */
export function chargeModeTimeZone() { return _timeZone; }

/**
 * Back to the state a freshly opened modal is in: New payment, balance side
 * dead, nothing known yet. Called by openChargeOneoffModal BEFORE the lookup.
 * @param {{now?: Date, timeZone?: string, balanceOnly?: boolean}} [opts]
 */
export function resetChargeModes(opts = {}) {
  _mode = 'new';
  _balanceOnly = !!opts.balanceOnly;
  // ⚠️ THE DESCRIPTION IS NEW PAYMENT'S AGAIN. The balance side locks it, and the window may have closed on that side
  // (Pay with another card always does): left locked, the next Charge card on file couldn't take a new payment.
  const desc = /** @type {HTMLInputElement|null} */ ($('co-description'));
  if (desc) desc.disabled = false;
  _owed = [];
  _totalCents = 0;
  _fetchedCard = null;
  _lookupOk = false;
  _newSide = { amount: '', description: '' };
  _now = opts.now || null;
  _timeZone = opts.timeZone || getCoachTimezone();

  const balanceBtn = $('co-mode-balance');
  if (balanceBtn) {
    balanceBtn.disabled = true;
    balanceBtn.textContent = 'Balance past due';
  }
  paintSides();
  note(LOOKING);
  showBalanceOnlyBits(false);
  refreshChargeUi();
}

/**
 * Ask what this client owes, and let the switch know.
 *
 * ⚠️ IT NEVER BLOCKS THE MODAL. A coach opening this to take a new payment
 * must not wait on, or be stopped by, a balance lookup.
 *
 * @param {string} clientId
 */
export async function loadBalanceForModal(clientId) {
  if (!can('financials.view')) { applyAmountOwed(null, new Error('no permission')); return; }
  try {
    const res = await callFunction('client-amount-owed', { clientId });
    if ($('co-client-id')?.value !== clientId) return; // modal moved on
    applyAmountOwed(res, null);
  } catch (err) {
    if ($('co-client-id')?.value !== clientId) return;
    applyAmountOwed(null, err);
  }
}

/**
 * @param {any|null} res the 200 body from client-amount-owed
 * @param {any|null} err whatever it threw instead
 */
export function applyAmountOwed(res, err) {
  _lookupOk = !err;
  _owed = (!err && res?.owed) ? res.owed : [];
  _totalCents = (!err && Number.isFinite(res?.totalCents)) ? res.totalCents : 0;
  _fetchedCard = (!err && res?.card) ? res.card : null;

  const btn = $('co-mode-balance');
  const usable = _lookupOk && _owed.length > 0 && _totalCents > 0;
  if (btn) {
    btn.disabled = !usable;
    btn.textContent = usable
      ? `Balance past due · ${owedMoneyCents(_totalCents)}`
      : 'Balance past due';
  }
  note(usable ? '' : (_lookupOk ? NO_BALANCE : COULD_NOT_CHECK));
  refreshChargeUi();
}

/**
 * Flip the switch. A dead side does nothing at all — the click never had a
 * meaning, so it must not half-happen.
 * @param {'new'|'balance'} mode
 */
export function setChargeMode(mode) {
  if (mode === _mode) return;
  if (mode === 'balance' && $('co-mode-balance')?.disabled) return;
  const amount = $('co-amount');
  const desc = $('co-description');

  if (mode === 'balance') {
    // Remember what New payment had, so flipping back and forth does not eat
    // what the coach already typed.
    _newSide = { amount: amount?.value || '', description: desc?.value || '' };
    _mode = 'balance';
    if (amount) amount.value = dollarsFor(_totalCents);
    if (desc) { desc.value = BALANCE_DESCRIPTION; desc.disabled = true; }
    renderOwedRows();
    showBalanceOnlyBits(true);
  } else {
    _mode = 'new';
    if (amount) amount.value = _newSide.amount;
    if (desc) { desc.value = _newSide.description; desc.disabled = false; }
    showBalanceOnlyBits(false);
  }
  paintSides();
  refreshChargeUi();
}

/**
 * Re-read the form and repaint everything that depends on it: the live line,
 * the two-line button, and whether the charge may go at all.
 *
 * ⚠️ NOT WHILE A CHARGE IS IN FLIGHT. `data-busy` is what says so — the button
 * label is the amount now, so its text can no longer be the flag.
 */
export function refreshChargeUi() {
  const btn = $('co-submit-btn');
  if (!btn || btn.dataset.busy === '1') return;

  const cents = centsTyped();
  const card = pickedCard();
  const parts = chargeButtonParts({ amountCents: cents, card });
  btn.innerHTML = chargeButtonHtml({ amountCents: cents, card });
  btn.setAttribute('aria-label', parts.ariaLabel);

  const hintEl = $('co-amount-hint');
  if (_mode !== 'balance') {
    // Today's behaviour: no ceiling, no live line, validation on submit. ⚠️ Never from "Pay with another card": there
    // the balance side is the only one, so until it is on (nothing owed, or no answer) nothing may be sent.
    if (hintEl) { hintEl.hidden = true; hintEl.textContent = ''; }
    btn.disabled = _balanceOnly;
    return;
  }

  const hint = balanceHint(_owed, cents, { ...(_now ? { now: _now } : {}), timeZone: _timeZone });
  if (hintEl) {
    hintEl.textContent = hint.sentence;
    hintEl.hidden = !hint.sentence;
  }
  // ⚠️ EDITABLE DOWN ONLY. The server refuses an overpay with a 400; the modal
  // refuses it first so the card is never touched for one.
  btn.disabled = !hint.ok;
}

/** The amount typed, in cents. */
export function centsTyped() {
  const raw = parseFloat($('co-amount')?.value || '');
  return Number.isFinite(raw) && raw > 0 ? Math.round(raw * 100) : 0;
}

/**
 * The card the second line names: whatever radio is ticked, else the card
 * client-amount-owed said the charge would hit. "New card" has no digits yet,
 * so it gets no second line rather than an invented one.
 */
export function pickedCard() {
  const checked = /** @type {HTMLInputElement|null} */ (document.querySelector('input[name="co-card"]:checked'));
  if (!checked) return _fetchedCard;
  if (checked.value === '__new__') return null;
  const row = checked.closest('.co-card-row');
  const brand = row?.querySelector('.co-card-brand')?.textContent?.trim() || '';
  const last4 = (String(row?.querySelector('.co-card-num')?.textContent || '').match(/(\d{4})\s*$/) || [])[1] || '';
  return last4 ? { brand, last4 } : _fetchedCard;
}

// ── the bits of DOM this side owns ───────────────────────────────────

function paintSides() {
  for (const [id, mine] of [['co-mode-new', 'new'], ['co-mode-balance', 'balance']]) {
    const el = $(id);
    if (!el) continue;
    const on = _mode === mine;
    el.classList.toggle('active', on);
    // The fill is the visual; this is what a screen reader gets, and what
    // makes the state readable without relying on the colour at all.
    el.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
}

/** @param {string} text '' hides it */
function note(text) {
  const el = $('co-balance-note');
  if (!el) return;
  el.textContent = text;
  el.hidden = !text;
}

/** @param {boolean} on */
function showBalanceOnlyBits(on) {
  const group = $('co-balance-group');
  if (group) group.hidden = !on;
  const hint = $('co-amount-hint');
  if (hint && !on) { hint.hidden = true; hint.textContent = ''; }
  // The receipt hint is about a line the COACH writes. On the balance side the
  // server writes it, so leaving the hint up would be a lie.
  const descHint = $('co-description-hint');
  if (descHint) descHint.hidden = on;
}

function renderOwedRows() {
  const list = $('co-balance-list');
  if (!list) return;
  const now = _now || new Date();
  list.innerHTML = _owed.map((r) => `
      <div class="co-balance-row">
        <span class="co-balance-date">${escHtml(owedDateLabel(owedDay(r), { now, timeZone: _timeZone }))}</span>
        <span class="co-balance-amount">${escHtml(owedMoneyCents(r.amountDueCents))}</span>
      </div>`).join('');
}

/** 60000 → "600", 20050 → "200.50" — what goes into a number input. */
function dollarsFor(cents) {
  return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
}
